import { useToast } from "@chakra-ui/react";
import { HTTPError } from "ky";
import { merge } from "lodash";
import { useMutation, UseMutationOptions, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";

import { useKy } from "src/common/ky";
import { DealerContact } from "src/common/types";
import {
  DEALER_ORGANIZATIONS_QUERY_KEY,
  DEALER_USERS_QUERY_KEY,
} from "src/routes/DealerOrganization/queries";

export const useDeleteDealerUserMutation = (
  options: UseMutationOptions<void, HTTPError, DealerContact> = {}
) => {
  const ky = useKy();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { id } = useParams<"id">();

  return useMutation(
    ({ dealerOrganizationId, dealerUserId }) => {
      return ky
        .delete(`dealer-users/${dealerUserId}`, {
          json: {
            dealerOrganizationId,
          },
        })
        .json();
    },
    merge(
      {
        onSuccess: async () => {
          toast({
            description: "Dealer User Removed",
            status: "success",
            duration: 5000,
            isClosable: true,
          });

          await queryClient.invalidateQueries(
            `${DEALER_ORGANIZATIONS_QUERY_KEY}:${id}`
          );

          await queryClient.invalidateQueries([
            DEALER_USERS_QUERY_KEY,
            `dealerOrganizationId=${id}`,
          ]);
        },
        onError: (error: Error) => {
          console.error(error);

          toast({
            description: "Unable to Remove Dealer User",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        },
      },
      options
    )
  );
};

export const useSetDealerContactMutation = (
  options: UseMutationOptions<void, HTTPError, DealerContact> = {}
) => {
  const ky = useKy();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { id } = useParams<"id">();

  return useMutation(
    ({ dealerOrganizationId, dealerUserId }) => {
      return ky
        .patch(`dealer-organizations/${dealerOrganizationId}/poc`, {
          json: { dealerUserId },
        })
        .json();
    },
    merge(
      {
        onSuccess: async () => {
          toast({
            description: "Successfully Updated Dealer Contact",
            status: "success",
            duration: 5000,
            isClosable: true,
          });

          await queryClient.invalidateQueries(
            `${DEALER_ORGANIZATIONS_QUERY_KEY}:${id}`
          );

          await queryClient.invalidateQueries([
            DEALER_USERS_QUERY_KEY,
            `dealerOrganizationId=${id}`,
          ]);
        },

        onError: () => {
          toast({
            description: "Unable to Set Dealer Contact",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        },
      },
      options
    )
  );
};

export const useResetPasswordMutation = (
  options: UseMutationOptions<void, HTTPError, string> = {}
) => {
  const ky = useKy();
  const toast = useToast();

  return useMutation(
    (dealerUserId) => {
      return ky.post(`dealer-users/${dealerUserId}/reset-password`).json();
    },
    merge(
      {
        onSuccess: () => {
          toast({
            description: "Password Reset Email Sent",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        },
        onError: (error: Error) => {
          toast({
            description: error.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        },
      },
      options
    )
  );
};
